/* One chip for every state in the two admin apps.
 *
 * TWO RULES, BOTH NON-NEGOTIABLE
 * ------------------------------
 * 1. COLOUR IS NEVER THE ONLY CHANNEL. The product owner has red-green
 *    colour blindness, so every chip carries a glyph and a word. The
 *    semantic pair is the ``band`` palette — teal #009E73 against deep
 *    red #B42318 — because the classic #059669 / #EF4444 pair is exactly
 *    the one deuteranopia cannot separate. Same rule the chart bands
 *    already follow.
 *
 * 2. A BACKEND IDENTIFIER NEVER REACHES THE SCREEN, AND A MISSING LABEL
 *    IS LOUD. The audit found ``completed``, ``schedule_queued``,
 *    ``provision``, ``active``, ``reload``, ``view_data`` and
 *    ``run_ai_analysis`` rendered raw. ``resolveStatus`` maps a raw value
 *    through a caller-supplied dictionary; when the key is missing it
 *    returns ``unknown: true`` and the chip renders the raw key with a
 *    warning glyph and a title that says the label is missing. It does
 *    NOT prettify the key into something plausible — an invented label
 *    is a silent wrong answer, and this codebase does not do fallbacks.
 */

import { AlertTriangle, Check, Circle, Info, Loader2, Minus, X } from "lucide-react";
import type { ReactNode } from "react";

import { cx } from "./layout";

export type StatusTone =
  | "neutral"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "running";

const TONE_CLASS: Record<StatusTone, string> = {
  // Deliberately muted surfaces: a table full of chips should read as a
  // table, not as a warning light.
  neutral: "bg-neutral-100 text-neutral-700 ring-neutral-200",
  // Look v3: info and running use the quiet violet of the system.
  info: "bg-primary-soft text-primary-ink ring-primary-line",
  // Look v3 Fase 1: the kit's success and error pairs (soft surface,
  // dark text); the glyph and the word carry the state too.
  success: "bg-success-soft text-success-dark ring-success/30",
  warning: "bg-warning-soft text-warning-dark ring-warning/30",
  danger: "bg-error-soft text-error-dark ring-error/30",
  running: "bg-primary-soft text-primary ring-primary-line",
};

const TONE_ICON: Record<StatusTone, ReactNode> = {
  neutral: <Minus size={12} strokeWidth={3} />,
  info: <Info size={12} strokeWidth={3} />,
  success: <Check size={12} strokeWidth={3} />,
  warning: <AlertTriangle size={12} strokeWidth={3} />,
  danger: <X size={12} strokeWidth={3} />,
  running: <Loader2 size={12} strokeWidth={3} className="animate-spin" />,
};

export interface StatusChipProps {
  /** The human label. Already translated by the caller. */
  label: string;
  tone?: StatusTone;
  /** Overrides the tone glyph. The glyph is never removed. */
  icon?: ReactNode;
  /** Marks the label as an unresolved backend key: renders the raw value
   *  with a warning glyph instead of pretending it is a label. */
  unknown?: boolean;
  /** Native tooltip — used to explain an unknown key. */
  title?: string;
  className?: string;
}

export function StatusChip({
  label,
  tone = "neutral",
  icon,
  unknown = false,
  title,
  className,
}: StatusChipProps): ReactNode {
  const effectiveTone: StatusTone = unknown ? "warning" : tone;
  return (
    <span
      // The tone is part of the contract (a warning stays a warning when
      // the palette changes): tests and styles read it from here.
      data-tone={effectiveTone}
      className={cx(
        "inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold ring-1 ring-inset",
        TONE_CLASS[effectiveTone],
        className,
      )}
      title={title}
    >
      <span className="flex-shrink-0" aria-hidden="true">
        {unknown ? <AlertTriangle size={12} strokeWidth={3} /> : (icon ?? TONE_ICON[tone])}
      </span>
      <span className={cx("truncate", unknown && "font-mono")}>{label}</span>
    </span>
  );
}

/** One entry of a status dictionary: how a raw backend value is shown. */
export interface StatusDef {
  label: string;
  tone: StatusTone;
}

export interface ResolvedStatus extends StatusDef {
  /** True when the raw value had no entry in the dictionary. */
  unknown: boolean;
  /** Tooltip text for an unknown value; undefined otherwise. */
  title?: string;
}

/**
 * Maps a raw backend value through a dictionary.
 *
 * A miss is not smoothed over: the raw value comes back with
 * ``unknown: true`` so the chip can show it as a defect. Callers pass
 * ``missingHint`` (already translated) to say so in the tooltip.
 */
export function resolveStatus(
  raw: string | null | undefined,
  dictionary: Record<string, StatusDef>,
  missingHint?: string,
): ResolvedStatus {
  if (raw === null || raw === undefined || raw === "") {
    return { label: "—", tone: "neutral", unknown: false };
  }
  const hit = dictionary[raw];
  if (hit !== undefined) {
    return { ...hit, unknown: false };
  }
  return {
    label: raw,
    tone: "warning",
    unknown: true,
    title: missingHint,
  };
}

/**
 * A dot-and-word chip for on/off states, where a full status chip is
 * too much furniture. Still carries a glyph.
 */
export function OnOffChip({
  on,
  onLabel,
  offLabel,
}: {
  on: boolean;
  onLabel: string;
  offLabel: string;
}): ReactNode {
  return (
    <StatusChip
      label={on ? onLabel : offLabel}
      tone={on ? "success" : "neutral"}
      icon={on ? <Check size={12} strokeWidth={3} /> : <Circle size={12} strokeWidth={3} />}
    />
  );
}
